/**
 * JPEG marker walk — ported from the DI-T1 spike
 * (`_SPIKES/browser-intake-benchmarks/src/jpeg/segments.ts`).
 *
 * The walk never touches pixel data: it stops at the first SOS, and everything
 * from there to EOI is scan data (the payload hash covers exactly that, DI-D4
 * c1). Measured by T1 on 918 files: both APP1 segments sit inside the first
 * 64 KiB on the P1 and the Sony (max SOS offset 41 794 on the P1, 57 073 on the
 * Sony). NOT on every family: the M4T visual frame carries a 53 KB APP7 before
 * its scan, so its SOS sits at 98 913 — its head parses, it just has no scan
 * inside the slice (`scan_not_in_slice`, measured 2026-09-24 by make-heads.mjs).
 *
 * Product change against the spike: the walk does not throw on a short buffer
 * or a lost marker sync — it reports what it found and where it stopped, so a
 * truncated or damaged file becomes a validity issue, not an exception. The
 * only throw is "no SOI".
 */

export const EXIF_SIG = "Exif\0\0";
// The two signatures are XML namespace identifiers written into the file, not
// URLs anything fetches — the https rule does not apply to them.
// eslint-disable-next-line no-restricted-syntax
export const XMP_SIG = "http://ns.adobe.com/xap/1.0/\0";
// eslint-disable-next-line no-restricted-syntax
export const XMP_EXT_SIG = "http://ns.adobe.com/xmp/extension/\0";

export interface Segment {
  /** offset of the 0xFF of the marker */
  offset: number;
  marker: number;
  /** the 2-byte length field (= payload + 2) */
  length: number;
  /** payload bounds, excluding the length field */
  payloadStart: number;
  payloadEnd: number;
}

export interface JpegHead {
  segments: Segment[];
  exif?: Segment;
  xmp?: Segment;
  xmpExtended: Segment[];
  /** offset of the first SOS marker; -1 when it is not inside the buffer */
  sosOffset: number;
  /** dimensions from the first SOF marker, when it is inside the buffer */
  sof?: { width: number; height: number };
  /** true when the walk hit EOI before any SOS */
  sawEoi: boolean;
  /**
   * true when an APP1 segment starts inside the buffer but ends beyond it —
   * the signal to read a bigger head (`head_over_64k`).
   */
  app1Incomplete: boolean;
  /** true when ANY segment before SOS starts inside the buffer but ends beyond it */
  segmentIncomplete: boolean;
  /** true when the walk stopped on a non-marker byte */
  lostSync: boolean;
}

/**
 * How far from the end of a file its last EOI may sit. DJI M4T files carry a
 * trailer after the last image (measured 2026-09-24: 381 B on the V frame,
 * 221 B on the T frame; both files are 4 KiB-aligned), so "the file ends with
 * FF D9" is wrong for them. Entropy-coded scan data never contains FF D9
 * (0xFF is always stuffed with 00 or followed by RST0–7), so an FF D9 inside
 * the last 4 KiB means the image did end; a file cut inside its scan has none.
 */
export const EOI_WINDOW = 4096;

export function hasEoiNearEnd(bytes: Uint8Array): boolean {
  const from = Math.max(0, bytes.length - EOI_WINDOW);
  for (let i = bytes.length - 2; i >= from; i--) {
    if (bytes[i] === 0xff && bytes[i + 1] === 0xd9) return true;
  }
  return false;
}

export class NotAJpegError extends Error {
  constructor() {
    super("not a JPEG (no SOI)");
    this.name = "NotAJpegError";
  }
}

/** Markers that carry no length field. */
const STANDALONE = new Set([
  0x01, 0xd0, 0xd1, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7,
]);

/** SOF0…SOF15 minus DHT (C4), JPG (C8) and DAC (CC). */
function isSof(marker: number): boolean {
  return (
    marker >= 0xc0 &&
    marker <= 0xcf &&
    marker !== 0xc4 &&
    marker !== 0xc8 &&
    marker !== 0xcc
  );
}

export function startsWith(buf: Uint8Array, at: number, sig: string): boolean {
  if (at + sig.length > buf.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (buf[at + i] !== sig.charCodeAt(i)) return false;
  }
  return true;
}

export function isJpeg(buf: Uint8Array): boolean {
  return buf.length >= 2 && buf[0] === 0xff && buf[1] === 0xd8;
}

/** Walks the segments of a JPEG (or of its head slice) up to the first SOS. */
export function walkHead(buf: Uint8Array): JpegHead {
  if (!isJpeg(buf)) throw new NotAJpegError();
  const out: JpegHead = {
    segments: [],
    xmpExtended: [],
    sosOffset: -1,
    sawEoi: false,
    app1Incomplete: false,
    segmentIncomplete: false,
    lostSync: false,
  };
  let i = 2;
  while (i < buf.length - 1) {
    if (buf[i] !== 0xff) {
      out.lostSync = true;
      break;
    }
    let j = i + 1;
    while (j < buf.length && buf[j] === 0xff) j++;
    if (j >= buf.length) break;
    const marker = buf[j] ?? 0;
    if (STANDALONE.has(marker)) {
      i = j + 1;
      continue;
    }
    if (marker === 0xd9) {
      out.sawEoi = true;
      break;
    }
    if (j + 2 >= buf.length) break;
    const length = ((buf[j + 1] ?? 0) << 8) | (buf[j + 2] ?? 0);
    const seg: Segment = {
      offset: i,
      marker,
      length,
      payloadStart: j + 3,
      payloadEnd: j + 1 + length,
    };
    if (marker === 0xda) {
      out.segments.push(seg);
      out.sosOffset = i;
      break;
    }
    if (seg.payloadEnd > buf.length) {
      // The segment starts here but ends beyond the bytes we hold.
      if (marker === 0xe1) out.app1Incomplete = true;
      out.segmentIncomplete = true;
      break;
    }
    out.segments.push(seg);
    if (marker === 0xe1) {
      if (startsWith(buf, seg.payloadStart, EXIF_SIG)) out.exif ??= seg;
      else if (startsWith(buf, seg.payloadStart, XMP_SIG)) out.xmp ??= seg;
      else if (startsWith(buf, seg.payloadStart, XMP_EXT_SIG))
        out.xmpExtended.push(seg);
    } else if (isSof(marker) && !out.sof && length >= 7) {
      const p = seg.payloadStart;
      out.sof = {
        height: ((buf[p + 1] ?? 0) << 8) | (buf[p + 2] ?? 0),
        width: ((buf[p + 3] ?? 0) << 8) | (buf[p + 4] ?? 0),
      };
    }
    i = seg.payloadEnd;
  }
  return out;
}

/** Writes a 0xFFEn segment header with the given payload length. */
export function segmentHeader(
  marker: number,
  payloadLength: number,
): Uint8Array {
  const total = payloadLength + 2;
  if (total > 0xffff)
    throw new Error(
      `APP segment would be ${String(total)} B, over the 64 KB limit`,
    );
  return new Uint8Array([0xff, marker, (total >> 8) & 0xff, total & 0xff]);
}

/**
 * TypeScript models `Uint8Array` as possibly SharedArrayBuffer-backed, which
 * the DOM signatures reject. Nothing here is ever shared-backed.
 */
export const bufferSource = (b: Uint8Array): BufferSource => b;
